import { useMemo } from 'react';
import { isDefined } from '@mews/std';
import { dateTimeToUtc, useCommanderSelector } from '@mews-commander/utils';
import { getAvailabilityAdjustmentsUniqResourceCategoryIds } from '@mews-commander/legacy-entities/src/ducks/availabilityAdjustments';
import { useResourceCategories } from '@mews-commander/legacy-entities/src/utils/useResourceCategory';
import { AvailabilityAdjustmentFormValues } from '../../../../types';
import {
    getCurrentEnterpriseMinimalOperationEditableUtc,
    getDateTimeRangeArray,
    getResourceCategoryAvailabilities,
} from '../../../../selectors';
import { getResourceCategoryBlockAdjustmentValue } from '../../../../utils';
import { DEFAULT_PAX_COUNT } from '../../../../constants';

export const useSetupMatrixInitialValues = (
    availabilityBlockId: string,
    serviceId: string
): AvailabilityAdjustmentFormValues => {
    const dateTimeRangeArray = useCommanderSelector((state) =>
        getDateTimeRangeArray(state, { id: availabilityBlockId, serviceId })
    );
    const resourceCategoryIds = useCommanderSelector((state) =>
        getAvailabilityAdjustmentsUniqResourceCategoryIds(state, { availabilityBlockId })
    );
    const availabilities = useCommanderSelector(getResourceCategoryAvailabilities);
    const editableHistoryUtc = useCommanderSelector(getCurrentEnterpriseMinimalOperationEditableUtc);

    const resourceCategories = useResourceCategories(resourceCategoryIds, serviceId);

    return useMemo(() => {
        const utcRange = dateTimeRangeArray.map(dateTimeToUtc);

        if (resourceCategoryIds.length > 0) {
            return {
                fieldArrayAvailability: resourceCategoryIds.map((resourceCategoryId) => {
                    const resourceCategory = resourceCategories[resourceCategoryId];

                    // for now just find first non empty paxCount
                    const foundPaxCounts = availabilities[resourceCategoryId]
                        ?.flatMap(
                            (item) =>
                                item.blockAdjustmentData?.flatMap((data) => {
                                    if (data.availabilityBlockId === availabilityBlockId && data.paxCounts) {
                                        return data.paxCounts;
                                    }

                                    return undefined;
                                })
                        )
                        .filter(isDefined);

                    return {
                        resourceCategoryId,
                        paxCount: foundPaxCounts?.[0] || {
                            personCount: resourceCategory?.capacity ?? 1,
                            unitCount: DEFAULT_PAX_COUNT.unitCount,
                        },
                        availabilities: utcRange.map((utc) => ({
                            disabled: utc < editableHistoryUtc,
                            dateTimeUtcForm: utc,
                            adjustmentValue: {
                                value: getResourceCategoryBlockAdjustmentValue({
                                    categoryAvailabilities: availabilities[resourceCategoryId],
                                    dateTimeUtc: utc,
                                    availabilityBlockId,
                                }),
                            },
                        })),
                    };
                }),
            };
        }

        return {
            fieldArrayAvailability: [
                {
                    resourceCategoryId: '',
                    paxCount: DEFAULT_PAX_COUNT,
                    availabilities: utcRange.map((utc) => ({
                        disabled: utc < editableHistoryUtc,
                        dateTimeUtcForm: utc,
                        adjustmentValue: { value: 0 },
                    })),
                },
            ],
        };
    }, [
        availabilities,
        availabilityBlockId,
        dateTimeRangeArray,
        editableHistoryUtc,
        resourceCategories,
        resourceCategoryIds,
    ]);
};
